import { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { relatedDestinationsForToCity } from "../data/featuredNetwork";

/**
 * Compact destination strip under search results.
 * Prefills "to" in the main search bar so the user keeps date / passengers.
 */
export default function RelatedDestinationsStrip({ excludeToCityName }) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const related = useMemo(
    () => relatedDestinationsForToCity(excludeToCityName),
    [excludeToCityName]
  );

  const items = related.items;
  if (!items.length) return null;

  const title =
    related.mode === "country" && related.countryKey
      ? t("home.relatedInCountry", {
          country: t(`popularTrips.countries.${related.countryKey}`),
        })
      : t("home.otherDestinations");

  const openInSearch = (destination) => {
    const params = new URLSearchParams(searchParams);
    params.set("to", destination.name);
    params.delete("focus");
    if (!params.get("from")) params.set("focus", "from");
    navigate(`/?${params.toString()}`);
    window.setTimeout(() => {
      document
        .getElementById("home-trip-search")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, py: 2 }}>
      <Typography variant="h6" fontWeight={700} mb={2} color="text.primary">
        {title}
      </Typography>

      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "auto",
          overscrollBehaviorX: "contain",
          pb: 1,
          scrollSnapType: "x mandatory",
          "&::-webkit-scrollbar": { height: 6 },
        }}
      >
        {items.map((dest) =>
          dest.image ? (
            <Box
              key={dest.key}
              component="button"
              type="button"
              onClick={() => openInSearch(dest)}
              sx={{
                flex: "1 1 140px",
                minWidth: 140,
                maxWidth: 220,
                scrollSnapAlign: "start",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
                p: 0,
                cursor: "pointer",
                bgcolor: "background.paper",
                textAlign: "left",
                font: "inherit",
                color: "inherit",
                transition: "border-color 0.2s, transform 0.2s",
                "&:hover": {
                  borderColor: "primary.main",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Box
                component="img"
                src={dest.image}
                alt={t(`popularTrips.cities.${dest.key}`)}
                loading="lazy"
                sx={{
                  width: "100%",
                  height: 88,
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <Box sx={{ px: 1.25, py: 1 }}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {t(`popularTrips.cities.${dest.key}`)}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {t(`popularTrips.countries.${dest.countryKey}`)}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              key={dest.key}
              component="button"
              type="button"
              onClick={() => openInSearch(dest)}
              sx={{
                flex: "1 1 140px",
                minWidth: 140,
                maxWidth: 220,
                scrollSnapAlign: "start",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 1.5,
                minHeight: 88,
                cursor: "pointer",
                bgcolor: "background.paper",
                textAlign: "left",
                font: "inherit",
                color: "inherit",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                transition: "border-color 0.2s, transform 0.2s",
                "&:hover": {
                  borderColor: "primary.main",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Typography variant="body2" fontWeight={700} noWrap>
                {t(`popularTrips.cities.${dest.key}`)}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {t(`popularTrips.countries.${dest.countryKey}`)}
              </Typography>
            </Box>
          )
        )}
      </Stack>
    </Box>
  );
}
